import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Paper,
  Divider,
  Grid,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Avatar,
  CircularProgress,
  Tooltip,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Storefront as StorefrontIcon,
  Restaurant as RestaurantIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Map as MapIcon,
  Image as ImageIcon,
  Fastfood as FastfoodIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  VpnKey as VpnKeyIcon,
  ContentCopy as ContentCopyIcon,
  RotateLeft as RotateLeftIcon,
  Block as BlockIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import api from '../services/api';
import ImageUpload from '../components/ImageUpload';
import GoogleMapModal from '../components/GoogleMapModal';

const AdminMenuEdit = ({ keywordsData, language }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [selectedRestaurantForApiKey, setSelectedRestaurantForApiKey] = useState(null);
  const [apiKeyData, setApiKeyData] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  const [formData, setFormData] = useState({
    id: null,
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    phone: '',
    email: '',
    address: '',
    address_en: '',
    city: '',
    area: '',
    latitude: null,
    longitude: null,
    cuisine_type: '',
    currency: 'MYR',
    delivery_fee: '0',
    min_order_amount: '0',
    delivery_time_min: '30',
    preparation_time_min: '15',
    logo: '',
    cover_image: '',
    is_active: true,
    is_approved: true,
    approval_status: 'approved',
    featured: false,
    rating: 0,
    total_reviews: 0,
    order_count: 0,
    view_count: 0,
    commission_percent: 10
  });

  const [apiKeyForm, setApiKeyForm] = useState({
    name: 'POS Print Service',
    permissions: {
      canPrint: true,
      canFetchOrders: true,
      canUpdateStatus: true,
      canFetchProducts: true,
      canFetchCategories: true
    },
    expiresInDays: null,
    ipWhitelist: []
  });

  const [ipInput, setIpInput] = useState('');

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const cuisineTypes = ['Restaurant', 'Pharmacy', 'Super market', 'Car', 'Home'];
  const currencies = ['EGP', 'USD', 'EUR', 'MYR', 'SAR'];

  useEffect(() => {
    fetchRestaurants();
    fetchStats();
  }, []);

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const response = await api.get('/restaurants/admin/all');
      setRestaurants(response.data.restaurants || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setMessage({
        type: 'error',
        text: language === 'ar' ? 'فشل في تحميل المطاعم' : 'Failed to load restaurants'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoDetect = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSelectedLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setOpenMap(true);
      },
      (error) => {
        alert(language === 'ar' ? keywordsData.enable_gps_ar : keywordsData.enable_gps_en);
      }
    );
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/restaurants/admin/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // ==================== POS API Key Management Functions ====================
  
  const handleOpenApiKeyDialog = async (restaurant) => {
    setSelectedRestaurantForApiKey(restaurant);
    setApiKeyDialogOpen(true);
    await fetchApiKey(restaurant.id);
  };

  const fetchApiKey = async (restaurantId) => {
    setApiKeyLoading(true);
    try {
      const response = await api.get(`/pos-api/restaurants/${restaurantId}/api-key`);
      setApiKeyData(response.data.data);
      setApiKeyForm({
        name: response.data.data.name || 'POS Print Service',
        permissions: response.data.data.permissions || {
          canPrint: true,
          canFetchOrders: true,
          canUpdateStatus: true,
          canFetchProducts: true,
          canFetchCategories: true
        },
        expiresInDays: response.data.data.expiresAt ? Math.ceil((new Date(response.data.data.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)) : null,
        ipWhitelist: response.data.data.ipWhitelist || []
      });
    } catch (error) {
      if (error.response?.status === 404) {
        setApiKeyData(null);
        setApiKeyForm({
          name: 'POS Print Service',
          permissions: {
            canPrint: true,
            canFetchOrders: true,
            canUpdateStatus: true,
            canFetchProducts: true,
            canFetchCategories: true
          },
          expiresInDays: null,
          ipWhitelist: []
        });
      } else {
        console.error('Error fetching API key:', error);
        setMessage({
          type: 'error',
          text: language === 'ar' ? 'فشل في تحميل مفتاح API' : 'Failed to load API key'
        });
      }
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    setApiKeyLoading(true);
    try {
      const response = await api.post(`/pos-api/restaurants/${selectedRestaurantForApiKey.id}/api-key`, {
        name: apiKeyForm.name,
        permissions: apiKeyForm.permissions,
        expiresInDays: apiKeyForm.expiresInDays || null,
        ipWhitelist: apiKeyForm.ipWhitelist
      });
      
      setApiKeyData(response.data.data);
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم إنشاء مفتاح API بنجاح' : 'API key created successfully'
      });
      
      // Show the secret key in a dialog
      alert(language === 'ar' 
        ? `تم إنشاء مفتاح API بنجاح\n\nAPI Key: ${response.data.data.apiKey}\nAPI Secret: ${response.data.data.apiSecret}\n\nيرجى حفظ هذه المعلومات في مكان آمن`
        : `API key created successfully\n\nAPI Key: ${response.data.data.apiKey}\nAPI Secret: ${response.data.data.apiSecret}\n\nPlease save this information in a secure place`
      );
    } catch (error) {
      console.error('Error creating API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في إنشاء مفتاح API' : 'Failed to create API key')
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleUpdateApiKey = async () => {
    setApiKeyLoading(true);
    try {
      const response = await api.put(`/pos-api/restaurants/${selectedRestaurantForApiKey.id}/api-key`, {
        name: apiKeyForm.name,
        permissions: apiKeyForm.permissions,
        ipWhitelist: apiKeyForm.ipWhitelist
      });
      
      setApiKeyData(response.data.data);
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم تحديث مفتاح API بنجاح' : 'API key updated successfully'
      });
    } catch (error) {
      console.error('Error updating API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في تحديث مفتاح API' : 'Failed to update API key')
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRotateApiKey = async () => {
    if (!window.confirm(language === 'ar' 
      ? 'تحذير: سيؤدي تدوير مفتاح API إلى تعطيل أي عملاء طباعة يستخدمون المفتاح الحالي. هل أنت متأكد؟'
      : 'Warning: Rotating the API key will disable any print clients using the current key. Are you sure?'
    )) return;
    
    setApiKeyLoading(true);
    try {
      const response = await api.post(`/pos-api/restaurants/${selectedRestaurantForApiKey.id}/api-key/rotate`);
      
      setApiKeyData(response.data.data);
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم تدوير مفتاح API بنجاح' : 'API key rotated successfully'
      });
      
      alert(language === 'ar'
        ? `تم تدوير مفتاح API\n\nالمفتاح الجديد: ${response.data.data.newApiKey}\nالسر الجديد: ${response.data.data.newApiSecret}\n\nيرجى تحديث عملاء الطباعة بالمفاتيح الجديدة`
        : `API key rotated successfully\n\nNew API Key: ${response.data.data.newApiKey}\nNew API Secret: ${response.data.data.newApiSecret}\n\nPlease update your print clients with the new keys`
      );
    } catch (error) {
      console.error('Error rotating API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في تدوير مفتاح API' : 'Failed to rotate API key')
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!window.confirm(language === 'ar'
      ? 'تحذير: سيؤدي إلغاء مفتاح API إلى تعطيل جميع عملاء الطباعة على الفور. هل أنت متأكد؟'
      : 'Warning: Revoking the API key will immediately disable all print clients. Are you sure?'
    )) return;
    
    setApiKeyLoading(true);
    try {
      await api.post(`/pos-api/restaurants/${selectedRestaurantForApiKey.id}/api-key/revoke`);
      
      await fetchApiKey(selectedRestaurantForApiKey.id);
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم إلغاء مفتاح API بنجاح' : 'API key revoked successfully'
      });
    } catch (error) {
      console.error('Error revoking API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في إلغاء مفتاح API' : 'Failed to revoke API key')
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!window.confirm(language === 'ar'
      ? 'تحذير: سيؤدي حذف مفتاح API إلى إزالته بشكل دائم. هل أنت متأكد؟'
      : 'Warning: Deleting the API key will permanently remove it. Are you sure?'
    )) return;
    
    setApiKeyLoading(true);
    try {
      await api.delete(`/pos-api/restaurants/${selectedRestaurantForApiKey.id}/api-key`);
      
      setApiKeyData(null);
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم حذف مفتاح API بنجاح' : 'API key deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في حذف مفتاح API' : 'Failed to delete API key')
      });
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({
      type: 'success',
      text: language === 'ar' ? 'تم النسخ إلى الحافظة' : 'Copied to clipboard'
    });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleAddIpToWhitelist = () => {
    if (ipInput && !apiKeyForm.ipWhitelist.includes(ipInput)) {
      setApiKeyForm(prev => ({
        ...prev,
        ipWhitelist: [...prev.ipWhitelist, ipInput]
      }));
      setIpInput('');
    }
  };

  const handleRemoveIpFromWhitelist = (ip) => {
    setApiKeyForm(prev => ({
      ...prev,
      ipWhitelist: prev.ipWhitelist.filter(i => i !== ip)
    }));
  };

  const handlePermissionChange = (permission) => (event) => {
    setApiKeyForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: event.target.checked
      }
    }));
  };

  // ==================== Restaurant Management Functions ====================

  const handleEditRestaurant = async (restaurant) => {
    setLoading(true);
    try {
      const response = await api.get(`/restaurants/${restaurant.id}`);
      const data = response.data.restaurant;
      setFormData({
        id: data.id,
        name: data.name || '',
        name_en: data.name_en || '',
        description: data.description || '',
        description_en: data.description_en || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        address_en: data.address_en || '',
        city: data.city || '',
        area: data.area || '',
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        cuisine_type: data.cuisine_type || '',
        currency: data.currency || 'MYR',
        delivery_fee: data.delivery_fee || '0',
        min_order_amount: data.min_order_amount || '0',
        delivery_time_min: data.delivery_time_min || '30',
        preparation_time_min: data.preparation_time_min || '15',
        logo: data.logo || '',
        cover_image: data.cover_image || '',
        is_active: data.is_active || false,
        is_approved: data.is_approved || false,
        approval_status: data.approval_status || 'pending',
        featured: data.featured || false,
        rating: data.rating || 0,
        total_reviews: data.total_reviews || 0,
        order_count: data.order_count || 0,
        view_count: data.view_count || 0,
        commission_percent: data.commission_percent || 10
      });
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching restaurant details:', error);
      setMessage({
        type: 'error',
        text: language === 'ar' ? 'فشل في تحميل تفاصيل المطعم' : 'Failed to load restaurant details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRestaurant = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        name_en: formData.name_en,
        description: formData.description,
        description_en: formData.description_en,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        address_en: formData.address_en,
        city: formData.city,
        area: formData.area,
        latitude: formData.latitude,
        longitude: formData.longitude,
        cuisine_type: formData.cuisine_type,
        currency: formData.currency,
        delivery_fee: formData.delivery_fee,
        min_order_amount: formData.min_order_amount,
        delivery_time_min: formData.delivery_time_min,
        preparation_time_min: formData.preparation_time_min,
        logo: formData.logo,
        cover_image: formData.cover_image,
        is_active: formData.is_active,
        is_approved: formData.is_approved,
        approval_status: formData.approval_status,
        featured: formData.featured,
        commission_percent: formData.commission_percent
      };

      await api.put(`/restaurants/admin/update/${formData.id}`, submitData);
      
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم تحديث المطعم بنجاح' : 'Restaurant updated successfully'
      });
      
      setEditDialogOpen(false);
      fetchRestaurants();
      fetchStats();
    } catch (error) {
      console.error('Error updating restaurant:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في تحديث المطعم' : 'Failed to update restaurant')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRestaurant = async () => {
    if (!restaurantToDelete) return;

    setLoading(true);
    try {
      await api.delete(`/restaurants/admin/delete/${restaurantToDelete.id}`);
      
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم حذف المطعم بنجاح' : 'Restaurant deleted successfully'
      });
      
      setDeleteDialogOpen(false);
      setRestaurantToDelete(null);
      fetchRestaurants();
      fetchStats();
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في حذف المطعم' : 'Failed to delete restaurant')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRestaurant = async (restaurantId, approved) => {
    setLoading(true);
    try {
      await api.put(`/restaurants/admin/approve/${restaurantId}`, {
        status: approved ? 'approved' : 'rejected'
      });
      
      setMessage({
        type: 'success',
        text: approved 
          ? (language === 'ar' ? 'تم قبول المطعم بنجاح' : 'Restaurant approved successfully')
          : (language === 'ar' ? 'تم رفض المطعم' : 'Restaurant rejected')
      });
      
      fetchRestaurants();
      fetchStats();
    } catch (error) {
      console.error('Error approving restaurant:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في معالجة الطلب' : 'Failed to process request')
      });
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = process.env.REACT_APP_MAP_API_KEY;
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API.");
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };

  const handleLocationSelect = (location) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(location));
      return;
    }
    setSelectedLocation(location);
    setFormData(prev => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
      address: location.address || prev.address
    }));
  };

  const handleImageChange = (field) => (imageUrl) => {
    setFormData(prev => ({ ...prev, [field]: imageUrl }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        if (!value) return language === 'ar' ? 'اسم المتجر مطلوب' : 'Store name is required';
        if (value.length < 3) return language === 'ar' ? 'اسم المتجر يجب أن يكون 3 أحرف على الأقل' : 'Store name must be at least 3 characters';
        return '';
      case 'phone':
        if (!value) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
        const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
        const phoneRegex = /^(\+?[0-9]{1,3})?[0-9]{7,12}$/;
        if (!phoneRegex.test(cleanPhone)) {
          return language === 'ar' 
            ? 'الرجاء إدخال رقم هاتف صالح (مثال: 0123456789 أو +20123456789)' 
            : 'Please enter a valid phone number (e.g., 0123456789 or +20123456789)';
        }
        const digitCount = cleanPhone.replace(/\+/g, '').length;
        if (digitCount < 7 || digitCount > 15) {
          return language === 'ar' 
            ? 'رقم الهاتف يجب أن يحتوي على 7-15 رقم' 
            : 'Phone number must contain 7-15 digits';
        }
        return '';
      case 'email':
        if (!value) return language === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
        return '';
      case 'address':
        if (!value) return language === 'ar' ? 'العنوان مطلوب' : 'Address is required';
        return '';
      case 'city':
        if (!value) return language === 'ar' ? 'المدينة مطلوبة' : 'City is required';
        return '';
      case 'latitude':
        if (!value) return language === 'ar' ? 'يرجى اختيار موقع المتجر على الخريطة' : 'Please select store location on map';
        return '';
      case 'commission_percent':
        if (value < 0 || value > 50) {
          return language === 'ar' ? 'نسبة العمولة يجب أن تكون بين 0 و 50' : 'Commission must be between 0 and 50';
        }
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const validateForm = () => {
    const newTouched = {};
    const newErrors = {};
    const requiredFields = ['name', 'phone', 'email', 'address', 'city', 'latitude'];
    
    requiredFields.forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    return !Object.values(newErrors).some(error => error);
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (restaurant.name_en && restaurant.name_en.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || restaurant.approval_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return <Chip label={language === 'ar' ? 'مقبول' : 'Approved'} color="success" size="small" />;
      case 'pending':
        return <Chip label={language === 'ar' ? 'قيد المراجعة' : 'Pending'} color="warning" size="small" />;
      case 'rejected':
        return <Chip label={language === 'ar' ? 'مرفوض' : 'Rejected'} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getApiKeyStatusChip = (status) => {
    switch (status) {
      case 'active':
        return <Chip label={language === 'ar' ? 'نشط' : 'Active'} color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'inactive':
        return <Chip label={language === 'ar' ? 'غير نشط' : 'Inactive'} color="warning" size="small" />;
      case 'revoked':
        return <Chip label={language === 'ar' ? 'ملغي' : 'Revoked'} color="error" size="small" icon={<BlockIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, mb: 10, backgroundColor: '#000000', minHeight: '100vh', color: '#FFFFFF' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorefrontIcon sx={{ fontSize: 48, color: '#FFD700' }} />
          <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 900, letterSpacing: '2px' }}>
            {language === 'ar' ? 'إدارة المطاعم' : 'Restaurant Management'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchRestaurants}
          sx={{
            backgroundColor: '#FFD700',
            color: '#000000',
            '&:hover': { backgroundColor: '#FFE55C' }
          }}
        >
          {language === 'ar' ? 'تحديث' : 'Refresh'}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>{stats.total}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إجمالي المطاعم' : 'Total Restaurants'}</Typography>
              </Box>
              <StorefrontIcon sx={{ fontSize: 48, color: '#FFD700', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 700 }}>{stats.pending}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'قيد المراجعة' : 'Pending'}</Typography>
              </Box>
              <AccessTimeIcon sx={{ fontSize: 48, color: '#FF9800', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>{stats.approved}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مقبول' : 'Approved'}</Typography>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#F44336', fontWeight: 700 }}>{stats.rejected}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مرفوض' : 'Rejected'}</Typography>
              </Box>
              <CancelIcon sx={{ fontSize: 48, color: '#F44336', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, backgroundColor: message.type === 'success' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 61, 0, 0.1)', color: message.type === 'success' ? '#FFD700' : '#FF3D00', border: `1px solid ${message.type === 'success' ? '#FFD700' : '#FF3D00'}`, borderRadius: '12px' }}
        >
          {message.text}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'بحث عن مطعم...' : 'Search restaurant...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الحالة' : 'Status'}</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label={language === 'ar' ? 'الحالة' : 'Status'}
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD700' },
                }}
              >
                <MenuItem value="all">{language === 'ar' ? 'الكل' : 'All'}</MenuItem>
                <MenuItem value="approved">{language === 'ar' ? 'مقبول' : 'Approved'}</MenuItem>
                <MenuItem value="pending">{language === 'ar' ? 'قيد المراجعة' : 'Pending'}</MenuItem>
                <MenuItem value="rejected">{language === 'ar' ? 'مرفوض' : 'Rejected'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Restaurants Table */}
      <Paper sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الصورة' : 'Image'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الاسم' : 'Name'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الهاتف' : 'Phone'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'المدينة' : 'City'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'النوع' : 'Type'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'التقييم' : 'Rating'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'إجراءات' : 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFD700' }} />
                  </TableCell>
                </TableRow>
              ) : filteredRestaurants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#B3B3B3' }}>
                    {language === 'ar' ? 'لا توجد مطاعم' : 'No restaurants found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRestaurants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((restaurant) => (
                  <TableRow key={restaurant.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.05)' } }}>
                    <TableCell>
                      <Avatar src={restaurant.logo} sx={{ width: 50, height: 50, borderRadius: 2 }}>
                        <RestaurantIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>{restaurant.name}</Typography>
                      {restaurant.name_en && (
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{restaurant.name_en}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#FFFFFF' }}>{restaurant.phone}</TableCell>
                    <TableCell sx={{ color: '#FFFFFF' }}>{restaurant.city}</TableCell>
                    <TableCell>
                      <Chip label={restaurant.cuisine_type || '-'} size="small" sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }} />
                    </TableCell>
                    <TableCell>{getStatusChip(restaurant.approval_status)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={restaurant.rating || 0} readOnly size="small" />
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>({restaurant.total_reviews || 0})</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={language === 'ar' ? 'مفتاح API' : 'API Key'}>
                          <IconButton onClick={() => handleOpenApiKeyDialog(restaurant)} sx={{ color: '#9C27B0' }}>
                            <VpnKeyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={language === 'ar' ? 'تعديل' : 'Edit'}>
                          <IconButton onClick={() => handleEditRestaurant(restaurant)} sx={{ color: '#FFD700' }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {restaurant.approval_status === 'pending' && (
                          <>
                            <Tooltip title={language === 'ar' ? 'قبول' : 'Approve'}>
                              <IconButton onClick={() => handleApproveRestaurant(restaurant.id, true)} sx={{ color: '#4CAF50' }}>
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={language === 'ar' ? 'رفض' : 'Reject'}>
                              <IconButton onClick={() => handleApproveRestaurant(restaurant.id, false)} sx={{ color: '#F44336' }}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={language === 'ar' ? 'حذف' : 'Delete'}>
                          <IconButton onClick={() => { setRestaurantToDelete(restaurant); setDeleteDialogOpen(true); }} sx={{ color: '#F44336' }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRestaurants.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ color: '#B3B3B3', borderTop: '1px solid #2D2D2D' }}
        />
      </Paper>

      {/* API Key Management Dialog */}
      <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700', borderBottom: '1px solid #2D2D2D' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon />
            {language === 'ar' ? 'إدارة مفتاح API' : 'API Key Management'}
            <Typography variant="body2" sx={{ color: '#B3B3B3', ml: 2 }}>
              {selectedRestaurantForApiKey?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => setApiKeyDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D', py: 3 }}>
          {apiKeyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : apiKeyData ? (
            // Existing API Key Display
            <Box>
              {/* API Key Info */}
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, fontWeight: 700 }}>
                  {language === 'ar' ? 'معلومات المفتاح' : 'Key Information'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>API Key</Typography>
                        <Typography variant="body2" sx={{ color: '#FFD700', fontFamily: 'monospace' }}>
                          {apiKeyData.apiKey}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => handleCopyToClipboard(apiKeyData.apiKey)} sx={{ color: '#FFD700' }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>API Secret</Typography>
                        <Typography variant="body2" sx={{ color: '#FF9800', fontFamily: 'monospace' }}>
                          {showApiSecret ? apiKeyData.apiSecret : '••••••••••••••••••••••••••••'}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton onClick={() => setShowApiSecret(!showApiSecret)} sx={{ color: '#FFD700' }}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton onClick={() => handleCopyToClipboard(apiKeyData.apiSecret)} sx={{ color: '#FFD700' }}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الحالة' : 'Status'}</Typography>
                    <Box>{getApiKeyStatusChip(apiKeyData.status)}</Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'آخر استخدام' : 'Last Used'}</Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                      {apiKeyData.lastUsedAt ? new Date(apiKeyData.lastUsedAt).toLocaleString() : (language === 'ar' ? 'لم يستخدم بعد' : 'Not used yet')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'تاريخ الإنشاء' : 'Created'}</Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                      {new Date(apiKeyData.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'تاريخ الانتهاء' : 'Expires'}</Typography>
                    <Typography variant="body2" sx={{ color: apiKeyData.expiresAt && new Date(apiKeyData.expiresAt) < new Date() ? '#F44336' : '#FFFFFF' }}>
                      {apiKeyData.expiresAt ? new Date(apiKeyData.expiresAt).toLocaleString() : (language === 'ar' ? 'لا ينتهي' : 'Never expires')}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Update Form */}
              <Accordion sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#FFD700' }} />}>
                  <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'تحديث الإعدادات' : 'Update Settings'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={language === 'ar' ? 'اسم المفتاح' : 'Key Name'}
                        value={apiKeyForm.name}
                        onChange={(e) => setApiKeyForm(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#0D0D0D', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{language === 'ar' ? 'الصلاحيات' : 'Permissions'}</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                            <Typography variant="body2">{language === 'ar' ? 'الطباعة' : 'Print'}</Typography>
                            <Switch checked={apiKeyForm.permissions.canPrint} onChange={handlePermissionChange('canPrint')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                            <Typography variant="body2">{language === 'ar' ? 'جلب الطلبات' : 'Fetch Orders'}</Typography>
                            <Switch checked={apiKeyForm.permissions.canFetchOrders} onChange={handlePermissionChange('canFetchOrders')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                            <Typography variant="body2">{language === 'ar' ? 'تحديث الحالة' : 'Update Status'}</Typography>
                            <Switch checked={apiKeyForm.permissions.canUpdateStatus} onChange={handlePermissionChange('canUpdateStatus')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                            <Typography variant="body2">{language === 'ar' ? 'جلب المنتجات' : 'Fetch Products'}</Typography>
                            <Switch checked={apiKeyForm.permissions.canFetchProducts} onChange={handlePermissionChange('canFetchProducts')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{language === 'ar' ? 'قائمة IP البيضاء' : 'IP Whitelist'}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={language === 'ar' ? 'أدخل عنوان IP' : 'Enter IP address'}
                          value={ipInput}
                          onChange={(e) => setIpInput(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#0D0D0D', color: '#FFFFFF' } }}
                        />
                        <Button variant="outlined" onClick={handleAddIpToWhitelist} sx={{ borderColor: '#FFD700', color: '#FFD700' }}>Add</Button>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {apiKeyForm.ipWhitelist.map((ip) => (
                          <Chip key={ip} label={ip} onDelete={() => handleRemoveIpFromWhitelist(ip)} sx={{ backgroundColor: '#FFD700', color: '#000000' }} />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleUpdateApiKey}
                        disabled={apiKeyLoading}
                        startIcon={<SaveIcon />}
                        sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' } }}
                      >
                        {apiKeyLoading ? <CircularProgress size={24} /> : (language === 'ar' ? 'تحديث' : 'Update')}
                      </Button>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Danger Zone */}
              <Paper sx={{ p: 3, backgroundColor: '#2D2D2D', border: '1px solid #F44336', borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ color: '#F44336', mb: 2, fontWeight: 700 }}>{language === 'ar' ? 'منطقة الخطر' : 'Danger Zone'}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Button fullWidth variant="outlined" startIcon={<RotateLeftIcon />} onClick={handleRotateApiKey} sx={{ borderColor: '#FF9800', color: '#FF9800' }}>
                      {language === 'ar' ? 'تدوير المفتاح' : 'Rotate Key'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button fullWidth variant="outlined" startIcon={<BlockIcon />} onClick={handleRevokeApiKey} sx={{ borderColor: '#F44336', color: '#F44336' }}>
                      {language === 'ar' ? 'إلغاء المفتاح' : 'Revoke Key'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button fullWidth variant="outlined" startIcon={<DeleteIcon />} onClick={handleDeleteApiKey} sx={{ borderColor: '#F44336', color: '#F44336' }}>
                      {language === 'ar' ? 'حذف المفتاح' : 'Delete Key'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          ) : (
            // Create New API Key
            <Box>
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ color: '#FFD700', mb: 2, fontWeight: 700 }}>
                  {language === 'ar' ? 'إنشاء مفتاح API جديد' : 'Create New API Key'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={language === 'ar' ? 'اسم المفتاح' : 'Key Name'}
                      value={apiKeyForm.name}
                      onChange={(e) => setApiKeyForm(prev => ({ ...prev, name: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#0D0D0D', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      label={language === 'ar' ? 'صلاحية (أيام)' : 'Expires In (Days)'}
                      placeholder={language === 'ar' ? 'اترك فارغاً لعدم الانتهاء' : 'Leave empty for never expire'}
                      value={apiKeyForm.expiresInDays || ''}
                      onChange={(e) => setApiKeyForm(prev => ({ ...prev, expiresInDays: e.target.value ? parseInt(e.target.value) : null }))}
                      sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#0D0D0D', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{language === 'ar' ? 'الصلاحيات' : 'Permissions'}</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                          <Typography variant="body2">{language === 'ar' ? 'الطباعة' : 'Print'}</Typography>
                          <Switch checked={apiKeyForm.permissions.canPrint} onChange={handlePermissionChange('canPrint')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                          <Typography variant="body2">{language === 'ar' ? 'جلب الطلبات' : 'Fetch Orders'}</Typography>
                          <Switch checked={apiKeyForm.permissions.canFetchOrders} onChange={handlePermissionChange('canFetchOrders')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                          <Typography variant="body2">{language === 'ar' ? 'تحديث الحالة' : 'Update Status'}</Typography>
                          <Switch checked={apiKeyForm.permissions.canUpdateStatus} onChange={handlePermissionChange('canUpdateStatus')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, backgroundColor: '#0D0D0D', borderRadius: '8px' }}>
                          <Typography variant="body2">{language === 'ar' ? 'جلب المنتجات' : 'Fetch Products'}</Typography>
                          <Switch checked={apiKeyForm.permissions.canFetchProducts} onChange={handlePermissionChange('canFetchProducts')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' } }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ color: '#FFD700', mb: 1 }}>{language === 'ar' ? 'قائمة IP البيضاء (اختياري)' : 'IP Whitelist (Optional)'}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={language === 'ar' ? 'أدخل عنوان IP' : 'Enter IP address'}
                        value={ipInput}
                        onChange={(e) => setIpInput(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#0D0D0D', color: '#FFFFFF' } }}
                      />
                      <Button variant="outlined" onClick={handleAddIpToWhitelist} sx={{ borderColor: '#FFD700', color: '#FFD700' }}>Add</Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {apiKeyForm.ipWhitelist.map((ip) => (
                        <Chip key={ip} label={ip} onDelete={() => handleRemoveIpFromWhitelist(ip)} sx={{ backgroundColor: '#FFD700', color: '#000000' }} />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCreateApiKey}
                disabled={apiKeyLoading}
                startIcon={<VpnKeyIcon />}
                sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' }, py: 1.5 }}
              >
                {apiKeyLoading ? <CircularProgress size={24} /> : (language === 'ar' ? 'إنشاء مفتاح API' : 'Create API Key')}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700', borderBottom: '1px solid #2D2D2D' }}>
          {language === 'ar' ? 'تعديل بيانات المطعم' : 'Edit Restaurant'}
          <IconButton onClick={() => setEditDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D', py: 3 }}>
          <Grid container spacing={2}>
            {/* Logo Upload */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', mb: 1 }}>{language === 'ar' ? 'شعار المتجر' : 'Store Logo'}</Typography>
              <ImageUpload
                value={formData.logo}
                onChange={handleImageChange('logo')}
                productName={formData.name}
                language={language}
                noTitle={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', mb: 1 }}>{language === 'ar' ? 'صورة الغلاف' : 'Cover Image'}</Typography>
              <ImageUpload
                value={formData.cover_image}
                onChange={handleImageChange('cover_image')}
                productName={formData.name}
                language={language}
                noTitle={true}
              />
            </Grid>

            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'اسم المتجر (عربي)' : 'Store Name (Arabic)'}
                value={formData.name}
                onChange={handleChange('name')}
                onBlur={handleBlur('name')}
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'اسم المتجر (إنجليزي)' : 'Store Name (English)'}
                value={formData.name_en}
                onChange={handleChange('name_en')}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'رقم الهاتف' : 'Phone'}
                value={formData.phone}
                onChange={handleChange('phone')}
                onBlur={handleBlur('phone')}
                error={touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'المدينة' : 'City'}
                value={formData.city}
                onChange={handleChange('city')}
                onBlur={handleBlur('city')}
                error={touched.city && !!errors.city}
                helperText={touched.city && errors.city}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'المنطقة' : 'Area'}
                value={formData.area}
                onChange={handleChange('area')}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={language === 'ar' ? 'العنوان' : 'Address'}
                value={formData.address}
                onChange={handleChange('address')}
                onBlur={handleBlur('address')}
                error={touched.address && !!errors.address}
                helperText={touched.address && errors.address}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAutoDetect}
                startIcon={<MapIcon />}
                sx={{ borderColor: '#FFD700', color: '#FFD700', '&:hover': { borderColor: '#FFE55C', backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}
              >
                {formData.latitude 
                  ? `${language === 'ar' ? 'تم اختيار الموقع' : 'Location Selected'} (${formData.latitude}, ${formData.longitude})`
                  : language === 'ar' ? 'اختر موقع المتجر على الخريطة' : 'Select store location on map'}
              </Button>
            </Grid>

            {/* Settings */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'نوع المتجر' : 'Store Type'}</InputLabel>
                <Select
                  value={formData.cuisine_type}
                  onChange={handleChange('cuisine_type')}
                  label={language === 'ar' ? 'نوع المتجر' : 'Store Type'}
                  sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' } }}
                >
                  {cuisineTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'العملة' : 'Currency'}</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={handleChange('currency')}
                  label={language === 'ar' ? 'العملة' : 'Currency'}
                  sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' } }}
                >
                  {currencies.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'رسوم التوصيل' : 'Delivery Fee'}
                type="number"
                value={formData.delivery_fee}
                onChange={handleChange('delivery_fee')}
                InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoneyIcon /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'الحد الأدنى للطلب' : 'Min Order'}
                type="number"
                value={formData.min_order_amount}
                onChange={handleChange('min_order_amount')}
                InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoneyIcon /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'وقت التوصيل (دقائق)' : 'Delivery Time (min)'}
                type="number"
                value={formData.delivery_time_min}
                onChange={handleChange('delivery_time_min')}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'نسبة العمولة (%)' : 'Commission (%)'}
                type="number"
                value={formData.commission_percent}
                onChange={handleChange('commission_percent')}
                onBlur={handleBlur('commission_percent')}
                error={touched.commission_percent && !!errors.commission_percent}
                helperText={touched.commission_percent && errors.commission_percent}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'حالة الموافقة' : 'Approval Status'}</InputLabel>
                <Select
                  value={formData.approval_status}
                  onChange={handleChange('approval_status')}
                  label={language === 'ar' ? 'حالة الموافقة' : 'Approval Status'}
                  sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' } }}
                >
                  <MenuItem value="approved">{language === 'ar' ? 'مقبول' : 'Approved'}</MenuItem>
                  <MenuItem value="pending">{language === 'ar' ? 'قيد المراجعة' : 'Pending'}</MenuItem>
                  <MenuItem value="rejected">{language === 'ar' ? 'مرفوض' : 'Rejected'}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#1A1A1A', borderRadius: '8px' }}>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'نشط' : 'Active'}</Typography>
                <Switch checked={formData.is_active} onChange={handleSwitchChange('is_active')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD700' } }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#1A1A1A', borderRadius: '8px' }}>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مميز' : 'Featured'}</Typography>
                <Switch checked={formData.featured} onChange={handleSwitchChange('featured')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD700' } }} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D', borderTop: '1px solid #2D2D2D', p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleUpdateRestaurant} variant="contained" disabled={loading} startIcon={<SaveIcon />} sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' } }}>
            {loading ? <CircularProgress size={24} sx={{ color: '#000000' }} /> : (language === 'ar' ? 'حفظ' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700' }}>
          {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D' }}>
          <Typography sx={{ color: '#FFFFFF' }}>
            {language === 'ar' 
              ? `هل أنت متأكد من حذف المطعم "${restaurantToDelete?.name}"؟`
              : `Are you sure you want to delete "${restaurantToDelete?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D', borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleDeleteRestaurant} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (language === 'ar' ? 'حذف' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Maps Modal */}
      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={(location) => handleLocationSelect(location)}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />
    </Container>
  );
};

export default AdminMenuEdit;